import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

/**
 * `/accounting/statements` — balance sheet, income statement and the
 * proposed closing entries.
 *
 * Statements are computed from the journal, not from stored balances.
 * When the balance sheet doesn't add up the page says so out loud — the
 * difference is an error in the posting, not in the sum.
 */

const searchSchema = z.object({ year: z.number().optional() });

type Line = { label: string; accounts: string[]; amount: string };

type Statements = {
  year: number;
  balanceSheet: {
    assets: Line[];
    liabilities: Line[];
    assetsTotal: string;
    liabilitiesTotal: string;
    difference: string;
    balanced: boolean;
    profit: string;
  };
  incomeStatement: {
    revenue: Line[];
    expenses: Line[];
    revenueTotal: string;
    expensesTotal: string;
    profit: string;
  };
};

type TaxLine = { label: string; amount: string; source: string };

type IncomeTax = {
  year: number;
  profit: string;
  base: string;
  rate: number;
  tax: string;
  lines: TaxLine[];
};

type FxProposal = {
  side: "receivable" | "payable";
  documentId: string;
  number: string | null;
  partnerName: string;
  currency: string;
  outstanding: string;
  originalRate: string;
  currentRate: string;
  difference: string;
};

type FxRevaluation = {
  asOf: string;
  proposals: FxProposal[];
  missingRates: string[];
  totalGain: string;
  totalLoss: string;
  posted: { id: string; number: number } | null;
};

type ClosingEntry = {
  description: string;
  debitCode: string;
  creditCode: string;
  amount: string;
};

export const Route = createFileRoute("/_app/accounting/statements")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  loader: async ({ deps }) => {
    try {
      const statements = await api.get<Statements>(
        `/api/accounting/statements${deps.year ? `?year=${deps.year}` : ""}`,
      );
      const tax = await api.get<IncomeTax>(`/api/accounting/income-tax?year=${statements.year}`);
      return { ...statements, tax };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: StatementsPage,
});

function money(amount: string): string {
  return Number(amount).toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function StatementsPage() {
  const data = Route.useLoaderData();
  const navigate = useNavigate();
  const [closing, setClosing] = useState<ClosingEntry[] | null>(null);

  const balance = data.balanceSheet;
  const income = data.incomeStatement;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Statements</h1>
          <p className="text-muted-foreground text-sm">
            Balance sheet and income statement in the short form of decree 500/2002 Coll., computed
            from the journal.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-sm">
            <span className="text-muted-foreground text-xs">Rok</span>
            <input
              type="number"
              defaultValue={data.year}
              className="mt-1 block h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
              onChange={(e) =>
                navigate({ to: "/accounting/statements", search: { year: Number(e.target.value) } })
              }
            />
          </label>
          <Button
            variant="outline"
            onClick={() =>
              api
                .get<{ entries: ClosingEntry[] }>(
                  `/api/accounting/statements/closing?year=${data.year}`,
                )
                .then((result) => setClosing(result.entries))
            }
          >
            Closing proposal
          </Button>
        </div>
      </div>

      {!balance.balanced && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">
              The balance sheet doesn't add up
            </CardTitle>
            <CardDescription>
              Assets and liabilities differ by {money(balance.difference)} Kč. The error is in the
              posting — go through the journal before deriving anything from this.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <StatementCard
          title="Aktiva"
          lines={balance.assets}
          total={balance.assetsTotal}
          totalLabel="Aktiva celkem"
        />
        <StatementCard
          title="Pasiva"
          lines={balance.liabilities}
          total={balance.liabilitiesTotal}
          totalLabel="Pasiva celkem"
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Profit or loss</CardTitle>
            <CardDescription>
              Revenue {money(income.revenueTotal)} − expenses {money(income.expensesTotal)}
            </CardDescription>
          </div>
          <div
            className={`font-semibold text-2xl ${
              Number(income.profit) >= 0 ? "text-emerald-600" : "text-destructive"
            }`}
          >
            {money(income.profit)} Kč
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <StatementTable lines={income.revenue} title="Revenue" total={income.revenueTotal} />
          <StatementTable lines={income.expenses} title="Expenses" total={income.expensesTotal} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">Income tax {data.tax.year}</CardTitle>
            <CardDescription>
              A working paper, not a return. Accounting profit is not the tax base — what the law
              doesn't recognise goes back on top, and the depreciation series are swapped.
            </CardDescription>
          </div>
          <div className="font-semibold text-xl">{money(data.tax.tax)} Kč</div>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <tbody>
              {data.tax.lines.map((line) => (
                <tr key={`${line.label}-${line.amount}`} className="border-t">
                  <td className="py-1 pr-4">
                    {line.label}
                    <span className="ml-2 text-muted-foreground text-xs">{line.source}</span>
                  </td>
                  <td className="py-1 text-right tabular-nums">{money(line.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <FxCard year={data.year} />

      {closing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Proposed closing entries for {data.year}</CardTitle>
            <CardDescription>
              Hub only proposes them. Closing the books is a decision — a person posts it in the
              journal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {closing.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nothing to transfer for this year.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {closing.map((entry) => (
                    <tr key={`${entry.debitCode}-${entry.creditCode}`} className="border-t">
                      <td className="py-1 pr-4">{entry.description}</td>
                      <td className="py-1 pr-4 tabular-nums">
                        MD {entry.debitCode} / D {entry.creditCode}
                      </td>
                      <td className="py-1 text-right tabular-nums">{money(entry.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Exchange rate differences at the balance sheet date.
 *
 * Proposed first, posted on request — a revaluation changes the result
 * of the year, so it is shown before it happens, not after.
 */
function FxCard({ year }: { year: number }) {
  const [asOf, setAsOf] = useState(`${year}-12-31`);
  const [data, setData] = useState<FxRevaluation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  async function load(date: string) {
    setError(null);
    try {
      setData(await api.get<FxRevaluation>(`/api/accounting/fx-revaluation?asOf=${date}`));
    } catch {
      setError("Couldn't compute the revaluation.");
    }
  }

  async function post() {
    setPosting(true);
    setError(null);
    try {
      await api.post("/api/accounting/fx-revaluation/post", { asOf });
      await load(asOf);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't post it.");
    } finally {
      setPosting(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Exchange rate differences</CardTitle>
          <CardDescription>
            What open foreign-currency documents are worth on the balance sheet date. The CNB rate
            of that day decides — Hub never invents one.
          </CardDescription>
        </div>
        <div className="flex items-end gap-2">
          <input
            type="date"
            value={asOf}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) => setAsOf(e.target.value)}
          />
          <Button variant="outline" onClick={() => load(asOf)}>
            Compute
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-destructive text-sm">{error}</p>}
        {data && data.missingRates.length > 0 && (
          <p className="text-amber-600 text-sm">
            The CNB has no fixing for {data.missingRates.join(", ")} on that day — pick the last
            banking day of the year.
          </p>
        )}
        {data && (
          <>
            <p className="text-muted-foreground text-sm">
              Gains {money(data.totalGain)} · losses {money(data.totalLoss)}
              {data.posted && ` · posted as entry no. ${data.posted.number}`}
            </p>
            {data.proposals.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nothing open in a foreign currency on that date.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-4 font-medium">Document</th>
                    <th className="py-1 pr-4 font-medium">Partner</th>
                    <th className="py-1 pr-4 text-right font-medium">Outstanding</th>
                    <th className="py-1 pr-4 text-right font-medium">Rate then → now</th>
                    <th className="py-1 text-right font-medium">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {data.proposals.map((proposal) => (
                    <tr key={proposal.documentId} className="border-t">
                      <td className="py-1 pr-4 font-mono text-xs">{proposal.number ?? "—"}</td>
                      <td className="py-1 pr-4">{proposal.partnerName}</td>
                      <td className="py-1 pr-4 text-right tabular-nums">
                        {money(proposal.outstanding)} {proposal.currency}
                      </td>
                      <td className="py-1 pr-4 text-right text-muted-foreground tabular-nums">
                        {Number(proposal.originalRate).toFixed(3)} →{" "}
                        {Number(proposal.currentRate).toFixed(3)}
                      </td>
                      <td className="py-1 text-right tabular-nums">{money(proposal.difference)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!data.posted && data.proposals.length > 0 && (
              <Button disabled={posting} onClick={() => post()}>
                {posting ? "Posting…" : "Post the difference"}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatementCard(props: {
  title: string;
  lines: Line[];
  total: string;
  totalLabel: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{props.title}</CardTitle>
        <Badge variant="secondary">{money(props.total)} Kč</Badge>
      </CardHeader>
      <CardContent>
        <StatementTable lines={props.lines} title={props.totalLabel} total={props.total} />
      </CardContent>
    </Card>
  );
}

function StatementTable({
  lines,
  title,
  total,
}: {
  lines: Line[];
  title: string;
  total: string;
}) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {lines.map((line) => (
          <tr key={line.label} className="border-t">
            <td className="py-1 pr-4">
              {line.label}
              {line.accounts.length > 0 && (
                <span className="ml-2 text-muted-foreground text-xs tabular-nums">
                  {line.accounts.join(", ")}
                </span>
              )}
            </td>
            <td className="py-1 text-right tabular-nums">{money(line.amount)}</td>
          </tr>
        ))}
        <tr className="border-t-2 font-semibold">
          <td className="py-1 pr-4">{title}</td>
          <td className="py-1 text-right tabular-nums">{money(total)}</td>
        </tr>
      </tbody>
    </table>
  );
}
